import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, CheckCircle2, XCircle, FileText, Upload, Eye } from 'lucide-react';
import { toast } from 'sonner';
import SearchBar from '../components/SearchBar';

const STATUS_COLORS = {
  Pending: 'bg-yellow-100 text-yellow-800',
  Approved: 'bg-green-100 text-green-800',
  Rejected: 'bg-red-100 text-red-800',
};
const STATUSES = ['Pending', 'Approved', 'Rejected'];

const blankItem = () => ({ description: '', qty: 1, unit: 'pcs', rate: 0 });
const blankForm = () => ({
  vendor_name: '', vendor_contact: '', vendor_email: '', quote_number: '',
  quote_date: new Date().toISOString().split('T')[0],
  items: [blankItem()], tax: 0, notes: ''
});

export default function QuotationsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [form, setForm] = useState(blankForm());
  const [approveDialog, setApproveDialog] = useState(null);
  const [approveNotes, setApproveNotes] = useState('');
  const [viewQuote, setViewQuote] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (searchText) params.set('search', searchText);
      const res = await api.get(`/quotations?${params}`);
      setRecords(res.data);
    } catch { toast.error('Failed to load quotations'); }
    finally { setLoading(false); }
  }, [statusFilter, searchText]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => { setEditId(null); setForm(blankForm()); setDialogOpen(true); };
  const openEdit = (rec) => {
    setEditId(rec._id);
    setForm({
      vendor_name: rec.vendor_name || '', vendor_contact: rec.vendor_contact || '',
      vendor_email: rec.vendor_email || '', quote_number: rec.quote_number || '',
      quote_date: rec.quote_date || '', items: rec.items?.length ? rec.items : [blankItem()],
      tax: rec.tax || 0, notes: rec.notes || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.vendor_name || !form.quote_number) { toast.error('Vendor and quote number required'); return; }
    try {
      if (editId) { await api.put(`/quotations/${editId}`, form); toast.success('Updated'); }
      else { await api.post('/quotations', form); toast.success('Quotation created'); }
      setDialogOpen(false);
      fetchData();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete quotation?')) return;
    try { await api.delete(`/quotations/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  const handleApprove = async () => {
    if (!approveDialog) return;
    try {
      const res = await api.post(`/quotations/${approveDialog._id}/approve`, { notes: approveNotes });
      toast.success(`Approved. PO ${res.data.po_number} generated`);
      setApproveDialog(null); setApproveNotes(''); fetchData();
    } catch { toast.error('Approval failed'); }
  };

  const handleReject = async (rec) => {
    const notes = window.prompt('Rejection reason (optional):') ?? '';
    try {
      await api.post(`/quotations/${rec._id}/reject`, { notes });
      toast.success('Rejected'); fetchData();
    } catch { toast.error('Reject failed'); }
  };

  const handleFileUpload = async (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    try {
      await api.post(`/quotations/${id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document attached'); fetchData();
    } catch { toast.error('Upload failed'); }
  };

  const updateItem = (idx, field, value) => {
    setForm(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: field === 'qty' || field === 'rate' ? Number(value) : value };
      return { ...prev, items };
    });
  };
  const addItem = () => setForm(prev => ({ ...prev, items: [...prev.items, blankItem()] }));
  const removeItem = (idx) => setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));

  const subtotal = form.items.reduce((s, it) => s + (it.qty || 0) * (it.rate || 0), 0);
  const total = subtotal + (Number(form.tax) || 0);

  return (
    <div data-testid="quotations-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight">Quotations</h1>
          <p className="text-sm text-[#4F734F] mt-1">Inward quotes from vendors awaiting approval</p>
        </div>
        <Button onClick={openNew} className="btn-accent gap-2" data-testid="add-quote-btn"><Plus size={16} /> New Quotation</Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <SearchBar value={searchText} onChange={setSearchText} placeholder="Search vendor, quote no..." testId="quote-search" className="!flex-none w-full sm:w-80" />
        <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40 h-11 input-field"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-[#E6EDE6] rounded-sm overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead><tr className="bg-[#1B2A1B] text-white">
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Quote #</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Date</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Vendor</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Items</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider text-right">Total</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Status</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Attach</th>
            <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="text-center py-8 text-olive-400">Loading...</td></tr> :
            records.length === 0 ? <tr><td colSpan={8} className="text-center py-8 text-olive-400">No quotations</td></tr> :
            records.map(rec => (
              <tr key={rec._id} className="border-b border-[#E6EDE6] hover:bg-[#F8FAF7]" data-testid={`quote-row-${rec._id}`}>
                <td className="py-2 px-3 font-mono text-xs">{rec.quote_number}</td>
                <td className="py-2 px-3">{rec.quote_date}</td>
                <td className="py-2 px-3 font-medium">{rec.vendor_name}</td>
                <td className="py-2 px-3 text-xs">{rec.items?.length || 0} items</td>
                <td className="py-2 px-3 text-right font-mono">{(rec.total || 0).toLocaleString('en-IN')}</td>
                <td className="py-2 px-3"><span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_COLORS[rec.status] || 'bg-gray-100'}`}>{rec.status}</span></td>
                <td className="py-2 px-3">
                  <div className="flex items-center gap-1">
                    {(rec.attachments || []).map((doc, i) => (
                      <button key={i} onClick={async () => {
                        try {
                          const r = await api.get(`/quotations/${rec._id}/documents/${i}`, { responseType: 'blob' });
                          const url = URL.createObjectURL(new Blob([r.data]));
                          const a = document.createElement('a'); a.href = url; a.download = doc.filename; a.click(); URL.revokeObjectURL(url);
                        } catch { toast.error('Download failed'); }
                      }} className="p-1 hover:bg-olive-50 rounded-sm" title={doc.filename}>
                        <FileText size={13} className="text-[#D4AF37]" />
                      </button>
                    ))}
                    <label className="p-1 hover:bg-olive-50 rounded-sm cursor-pointer" title="Upload">
                      <Upload size={13} className="text-olive-500" />
                      <input type="file" className="hidden" data-testid={`quote-upload-${rec._id}`} onChange={(ev) => { const f = ev.target.files?.[0]; if (f) handleFileUpload(rec._id, f); ev.target.value = ''; }} />
                    </label>
                  </div>
                </td>
                <td className="py-2 px-3">
                  <div className="flex gap-0.5">
                    <button onClick={() => setViewQuote(rec)} className="p-1 hover:bg-olive-50 rounded-sm" title="View"><Eye size={13} className="text-olive-500" /></button>
                    {rec.status === 'Pending' && (
                      <>
                        <button onClick={() => openEdit(rec)} className="p-1 hover:bg-olive-100 rounded-sm" title="Edit"><Pencil size={13} className="text-olive-500" /></button>
                        {isAdmin && (
                          <>
                            <button onClick={() => { setApproveDialog(rec); setApproveNotes(''); }} className="p-1 hover:bg-green-50 rounded-sm" title="Approve" data-testid={`approve-quote-${rec._id}`}><CheckCircle2 size={13} className="text-green-600" /></button>
                            <button onClick={() => handleReject(rec)} className="p-1 hover:bg-red-50 rounded-sm" title="Reject" data-testid={`reject-quote-${rec._id}`}><XCircle size={13} className="text-red-500" /></button>
                          </>
                        )}
                      </>
                    )}
                    {isAdmin && <button onClick={() => handleDelete(rec._id)} className="p-1 hover:bg-red-50 rounded-sm" title="Delete"><Trash2 size={13} className="text-red-400" /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* New/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editId ? 'Edit' : 'New'} Quotation</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Vendor Name *</Label><Input value={form.vendor_name} onChange={(e) => setForm(p => ({ ...p, vendor_name: e.target.value }))} className="input-field" data-testid="quote-vendor" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Quote Number *</Label><Input value={form.quote_number} onChange={(e) => setForm(p => ({ ...p, quote_number: e.target.value }))} className="input-field" data-testid="quote-number" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Vendor Contact</Label><Input value={form.vendor_contact} onChange={(e) => setForm(p => ({ ...p, vendor_contact: e.target.value }))} className="input-field" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Vendor Email</Label><Input value={form.vendor_email} onChange={(e) => setForm(p => ({ ...p, vendor_email: e.target.value }))} className="input-field" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Quote Date</Label><Input type="date" value={form.quote_date} onChange={(e) => setForm(p => ({ ...p, quote_date: e.target.value }))} className="input-field" /></div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem} className="gap-1"><Plus size={13} /> Add Item</Button>
              </div>
              <div className="space-y-2">
                {form.items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_80px_80px_100px_100px_30px] gap-2 items-center">
                    <Input placeholder="Description" value={it.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} className="input-field" />
                    <Input type="number" placeholder="Qty" value={it.qty} onChange={(e) => updateItem(idx, 'qty', e.target.value)} className="input-field" />
                    <Input placeholder="Unit" value={it.unit} onChange={(e) => updateItem(idx, 'unit', e.target.value)} className="input-field" />
                    <Input type="number" placeholder="Rate" value={it.rate} onChange={(e) => updateItem(idx, 'rate', e.target.value)} className="input-field" />
                    <div className="text-right text-sm font-mono">{((it.qty || 0) * (it.rate || 0)).toLocaleString('en-IN')}</div>
                    {form.items.length > 1 && <button type="button" onClick={() => removeItem(idx)} className="p-1 hover:bg-red-50 rounded-sm"><Trash2 size={13} className="text-red-400" /></button>}
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Tax (INR)</Label><Input type="number" value={form.tax} onChange={(e) => setForm(p => ({ ...p, tax: Number(e.target.value) }))} className="input-field" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Total (auto)</Label><div className="h-10 flex items-center px-3 font-mono text-sm bg-[#F8FAF7] border border-[#E6EDE6] rounded-sm">INR {total.toLocaleString('en-IN')}</div></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} className="input-field" rows={2} /></div>
            <div className="flex gap-3 pt-2">
              <Button onClick={handleSubmit} className="btn-primary" data-testid="save-quote-btn">{editId ? 'Update' : 'Create'}</Button>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Approve dialog */}
      <Dialog open={!!approveDialog} onOpenChange={(o) => !o && setApproveDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl">Approve Quotation</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <p className="text-sm text-[#4F734F]">Approving <strong>{approveDialog?.quote_number}</strong> from <strong>{approveDialog?.vendor_name}</strong> will auto-generate a Purchase Order.</p>
            <Textarea placeholder="Approval notes (optional)" value={approveNotes} onChange={(e) => setApproveNotes(e.target.value)} className="input-field" rows={2} data-testid="approve-notes" />
            <div className="flex gap-2">
              <Button onClick={handleApprove} className="btn-primary gap-2" data-testid="confirm-approve-btn"><CheckCircle2 size={15} /> Approve & Generate PO</Button>
              <Button variant="outline" onClick={() => setApproveDialog(null)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewQuote} onOpenChange={(o) => !o && setViewQuote(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl">{viewQuote?.quote_number} — {viewQuote?.vendor_name}</DialogTitle></DialogHeader>
          {viewQuote && (
            <div className="space-y-3 mt-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-[#4F734F] text-xs uppercase">Date:</span> {viewQuote.quote_date}</div>
                <div><span className="text-[#4F734F] text-xs uppercase">Status:</span> <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_COLORS[viewQuote.status]}`}>{viewQuote.status}</span></div>
                <div><span className="text-[#4F734F] text-xs uppercase">Contact:</span> {viewQuote.vendor_contact || '-'}</div>
                <div><span className="text-[#4F734F] text-xs uppercase">Email:</span> {viewQuote.vendor_email || '-'}</div>
              </div>
              <table className="w-full text-sm border border-[#E6EDE6]">
                <thead className="bg-[#F8FAF7]"><tr><th className="p-2 text-left">#</th><th className="p-2 text-left">Description</th><th className="p-2">Qty</th><th className="p-2">Unit</th><th className="p-2 text-right">Rate</th><th className="p-2 text-right">Amount</th></tr></thead>
                <tbody>
                  {(viewQuote.items || []).map((it, i) => (
                    <tr key={i} className="border-t border-[#E6EDE6]">
                      <td className="p-2">{i + 1}</td><td className="p-2">{it.description}</td><td className="p-2 text-center">{it.qty}</td><td className="p-2 text-center">{it.unit}</td>
                      <td className="p-2 text-right font-mono">{(it.rate || 0).toLocaleString('en-IN')}</td>
                      <td className="p-2 text-right font-mono">{((it.qty || 0) * (it.rate || 0)).toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-right space-y-1">
                <div>Subtotal: <span className="font-mono">{(viewQuote.subtotal || 0).toLocaleString('en-IN')}</span></div>
                {viewQuote.tax ? <div>Tax: <span className="font-mono">{viewQuote.tax.toLocaleString('en-IN')}</span></div> : null}
                <div className="font-semibold text-lg">Total: <span className="font-mono">INR {(viewQuote.total || 0).toLocaleString('en-IN')}</span></div>
              </div>
              {viewQuote.notes && <div><span className="text-[#4F734F] text-xs uppercase">Notes:</span> <p className="text-sm">{viewQuote.notes}</p></div>}
              {viewQuote.approval_notes && <div><span className="text-[#4F734F] text-xs uppercase">{viewQuote.status} Notes:</span> <p className="text-sm">{viewQuote.approval_notes}</p> {viewQuote.approved_by && <p className="text-xs text-[#4F734F]">by {viewQuote.approved_by}</p>}</div>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
